import { addError } from "@wayflyer/blackbox-fe";
import { AppShell, useToast, type AppShellProps } from "@wayflyer/flyui";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useCallback, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Navigate, Outlet, useLocation, useMatch, useResolvedPath, useRoutes, type RouteObject } from "react-router";
import {
  ONBOARDING_STEP_IDS,
  ONBOARDING_SUBSTEP_IDS,
  OnboardingWorkflowTracker,
  UnknownRouteError,
  useSpaNavigate,
} from "shared-ui";
import { useCobrandingConfig, useCustomerAppConfig } from "@wayflyer/undercarriage";

import { ResetScrollPosition } from "./components";
import { CONNECT_APP_ROUTE_PATHS, NAVIGATION_PATHS, NEW_BANKING_ACCOUNTS_PAGE_FLAG } from "./components/constants";
import { BankingLeaveGuardContext, type BankingLeaveGuard } from "./hooks/banking-leave-guard-context";
import { FINANCIAL_DOCUMENTS_STEP_ID, LinearNavigationContextProvider } from "./hooks/navigation-next";
import { useSubmitUnsubmittedBankStatements } from "./hooks/use-submit-unsubmitted-bank-statements";
import {
  AboutYourBusinessPage,
  AdditionalFundingPage,
  AdditionalInformationRequestPage,
  AirsReviewPage,
  ApplicationReviewPage,
  BankingPage,
  ConfigureNewFunderCustomerPage,
  ConnectionManagerNext,
  CreditCheckConsentPage,
  DocumentUploadManager,
  FundingLandingPage,
  IneligiblePage,
  OriginConnectionCallbackPage,
  UploadBankStatementsPage,
  VerifyDocumentUploadsManager,
} from "./pages";
import { UploadDocumentType } from "./types";

const MESSAGES = defineMessages({
  submitStatementsFailedToast: {
    id: "funding-application: workflow tracker submit statements failed toast",
    defaultMessage: "We could not submit your bank statements. Try again.",
  },
});

const FundingApplicationLayout = ({
  countryOfIncorporation,
  customerDomain,
  secondaryLogoConfig,
  children,
}: {
  countryOfIncorporation?: string;
  customerDomain: string;
  secondaryLogoConfig?: AppShellProps["secondaryLogo"];
  children: React.ReactNode;
}) => {
  const location = useLocation();

  // Determine current step/substep based on page
  let currentStepId: string;
  let currentSubstepId: string | undefined;

  const isBusinessModelPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.BUSINESS_MODEL}`);

  if (isBusinessModelPage) {
    currentStepId = ONBOARDING_STEP_IDS.BUSINESS_DETAILS;
    currentSubstepId = ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL;
  } else {
    const searchParams = new URLSearchParams(location.search);
    const documentParam = searchParams.get("document");
    const isBankingPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.BANKING}`);
    const isCreditCheckPage = location.pathname.includes(
      `/${CONNECT_APP_ROUTE_PATHS.CONSENT}/${CONNECT_APP_ROUTE_PATHS.CREDIT_CHECK}`
    );
    const isUploadsPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.UPLOADS}`);
    const isConnectionsPage = location.pathname.includes(`/${CONNECT_APP_ROUTE_PATHS.CONNECTIONS}`);

    currentStepId = ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA;

    if (isBankingPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT;
    } else if (isConnectionsPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.PLATFORMS;
    } else if (isCreditCheckPage) {
      currentSubstepId = ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK;
    } else if (isUploadsPage && documentParam) {
      // pool-bancario is a legacy alias for the combined financial-documents step (see
      // document-uploads-manager.tsx) - normalize it so the sidebar highlights the right substep.
      currentSubstepId =
        documentParam === UploadDocumentType.POOL_BANCARIO ? FINANCIAL_DOCUMENTS_STEP_ID : documentParam;
    }
  }

  // Ineligible is a dead end, so the next step is shown as unavailable rather than in progress.
  const isIneligiblePage = useMatch(useResolvedPath(CONNECT_APP_ROUTE_PATHS.INELIGIBLE).pathname) !== null;

  const bankingLeaveGuardRef = useRef<BankingLeaveGuard | null>(null);
  const submitUnsubmittedBankStatements = useSubmitUnsubmittedBankStatements(customerDomain);
  const isNewBankingAccountsPage = useFeatureFlagEnabled(NEW_BANKING_ACCOUNTS_PAGE_FLAG) === true;
  const toaster = useToast();
  const intl = useIntl();

  const onBeforeLeaveBanking = useCallback(async () => {
    if (!isNewBankingAccountsPage) {
      return;
    }
    await bankingLeaveGuardRef.current?.();
    try {
      await submitUnsubmittedBankStatements();
    } catch (e: any) {
      toaster?.critical(intl.formatMessage(MESSAGES.submitStatementsFailedToast));
      addError(e, { message: "funding-application-layout: submit unsubmitted bank statements error" });
      throw e;
    }
  }, [isNewBankingAccountsPage, submitUnsubmittedBankStatements, toaster, intl]);

  return (
    <BankingLeaveGuardContext.Provider value={bankingLeaveGuardRef}>
      <OnboardingWorkflowTracker
        customerDomain={customerDomain}
        countryOfIncorporation={countryOfIncorporation}
        currentStepId={currentStepId}
        currentSubstepId={currentSubstepId}
        currentStepStatus={isIneligiblePage ? "neutral" : undefined}
        currentStepDisabled={isIneligiblePage}
        hasBusinessCountryData={true}
        hasRevenueData={true}
        hasBusinessLocationData={true}
        hasBusinessDetailsData={true}
        secondaryLogo={secondaryLogoConfig}
        onBeforeLeaveBanking={onBeforeLeaveBanking}
      >
        {children}
      </OnboardingWorkflowTracker>
    </BankingLeaveGuardContext.Provider>
  );
};

const Routes = () => {
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { data: cobrandingLogos } = useCobrandingConfig();

  const { navigate: navigateCompanyCreation } = useSpaNavigate("CompanyCreation");

  const secondaryLogoConfig = {
    alt: cobrandingLogos?.partnerName || "",
    lightIconLogoUrl: cobrandingLogos?.lightIconLogoUrl,
    darkIconLogoUrl: cobrandingLogos?.darkIconLogoUrl,
    lightLogoUrl: cobrandingLogos?.lightLogoUrl,
    darkLogoUrl: cobrandingLogos?.darkLogoUrl,
  };

  const applicationJourneyRoutes: RouteObject = {
    children: [
      {
        path: "/",
        element: (
          <LinearNavigationContextProvider
            countryOfIncorporation={countryOfIncorporation ?? undefined}
            customerDomain={customerDomain}
          >
            <FundingApplicationLayout
              countryOfIncorporation={countryOfIncorporation ?? undefined}
              customerDomain={customerDomain}
              secondaryLogoConfig={secondaryLogoConfig}
            >
              <Outlet />
            </FundingApplicationLayout>
          </LinearNavigationContextProvider>
        ),
        children: [
          {
            path: CONNECT_APP_ROUTE_PATHS.CONNECTIONS,
            children: [
              {
                path: CONNECT_APP_ROUTE_PATHS.ADD_CONNECTION,
                element: <ConnectionManagerNext />,
              },
              {
                path: CONNECT_APP_ROUTE_PATHS.BANKING,
                children: [
                  {
                    path: "",
                    element: <BankingPage />,
                  },
                  {
                    path: CONNECT_APP_ROUTE_PATHS.BANK_STATEMENTS_UPLOAD,
                    element: <UploadBankStatementsPage />,
                  },
                ],
              },
            ],
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.CONSENT,
            children: [
              {
                path: CONNECT_APP_ROUTE_PATHS.CREDIT_CHECK,
                element: <CreditCheckConsentPage />,
              },
            ],
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.UPLOADS,
            element: <DocumentUploadManager />,
          },
          {
            path: CONNECT_APP_ROUTE_PATHS.VERIFY,
            element: <VerifyDocumentUploadsManager />,
          },
        ],
      },
    ],
  };

  const routes: RouteObject[] = [
    // These routes should work outside a product request
    {
      path: "/",
      element: (
        <AppShell>
          <Outlet />
        </AppShell>
      ),
      children: [
        {
          path: "",
          element: <FundingLandingPage />,
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.ADDITIONAL_FUNDING,
          // TODO fix this one - slipped
          element: <AdditionalFundingPage />,
        },
      ],
    },
    applicationJourneyRoutes,
    {
      path: "/",
      element: (
        <AppShell>
          <Outlet />
        </AppShell>
      ),
      children: [
        {
          path: CONNECT_APP_ROUTE_PATHS.IN_REVIEW,
          element: <ApplicationReviewPage />,
        },
        {
          path: `${CONNECT_APP_ROUTE_PATHS.IN_REVIEW}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}`,
          element: <AirsReviewPage />,
        },
        // Post application AIRs
        {
          path: `${CONNECT_APP_ROUTE_PATHS.IN_REVIEW}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
        // Tasks pre-additional funding application AIRs
        {
          path: `${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_FUNDING}/${CONNECT_APP_ROUTE_PATHS.CUSTOMER_TASKS}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
        // Home page connection callback (redirects to /home after processing)
        {
          path: CONNECT_APP_ROUTE_PATHS.HOME,
          element: (
            <OriginConnectionCallbackPage
              airBasePath={NAVIGATION_PATHS.HOME_ADDITIONAL_INFORMATION_REQUEST}
              returnPath={CONNECT_APP_ROUTE_PATHS.HOME}
            />
          ),
        },
        {
          path: `${CONNECT_APP_ROUTE_PATHS.HOME}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
        // Financing dashboard connection callback (redirects to /financing after processing)
        {
          path: CONNECT_APP_ROUTE_PATHS.FINANCING,
          element: (
            <OriginConnectionCallbackPage
              airBasePath={NAVIGATION_PATHS.FINANCING_ADDITIONAL_INFORMATION_REQUEST}
              returnPath={CONNECT_APP_ROUTE_PATHS.FINANCING}
            />
          ),
        },
        // No AIR in flight to resume (e.g. an OAuth error with no persisted state) - back to the dashboard
        {
          path: `${CONNECT_APP_ROUTE_PATHS.FINANCING}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}`,
          element: <Navigate to={`/${customerDomain}/financing`} replace />,
        },
        {
          path: `${CONNECT_APP_ROUTE_PATHS.FINANCING}/${CONNECT_APP_ROUTE_PATHS.ADDITIONAL_INFORMATION_REQUEST}/:airId`,
          element: <AdditionalInformationRequestPage />,
        },
      ],
    },
    {
      path: "/",
      element: (
        <FundingApplicationLayout
          countryOfIncorporation={countryOfIncorporation ?? undefined}
          customerDomain={customerDomain}
          secondaryLogoConfig={secondaryLogoConfig}
        >
          <Outlet />
        </FundingApplicationLayout>
      ),
      children: [
        {
          path: CONNECT_APP_ROUTE_PATHS.BUSINESS_MODEL,
          element: <AboutYourBusinessPage onBack={() => navigateCompanyCreation("business-details")} />,
        },
        {
          path: CONNECT_APP_ROUTE_PATHS.INELIGIBLE,
          element: <IneligiblePage />,
        },
      ],
    },
    {
      path: CONNECT_APP_ROUTE_PATHS.CONFIGURE_NEW_FUNDER_CUSTOMER,
      element: <ConfigureNewFunderCustomerPage />,
    },
    {
      path: "*",
      element: <UnknownRouteError />,
    },
  ];
  return <>{useRoutes(routes)}</>;
};

export const FundingApplicationAppRouter = () => {
  return (
    <>
      <ResetScrollPosition />
      <Routes />
    </>
  );
};
